import { Link } from 'react-router-dom';
import { useStateContext } from '../../hooks/useStateContext';
import useSEO from '../../hooks/useSEO';
import { schemaGraph } from '../../utils/schema';
import { STATES } from '../../utils/states';

// Every live state, from the state config — a new launch shows up here automatically.
const LIVE_STATES = Object.values(STATES).filter(s => s.live);

// Rendered at /{state}/* for states without approved courses yet.
// Captures search intent, invites notification signup, and cross-sells Arizona (our live product).
export default function StateComingSoonPage() {
  const { state } = useStateContext();

  useSEO({
    title: `${state.name} Real Estate CE — Launching Soon | Steady Education`,
    description: `Steady Education is expanding to ${state.name}. ${state.regulator}-approved real estate continuing education, self-paced online, coming soon.`,
    url: `https://steadyeducation.com/${state.slug}`,
    jsonLd: schemaGraph({
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: state.name, url: `https://steadyeducation.com/${state.slug}` },
      ],
    }),
  });

  return (
    <div style={styles.page}>
      <div className="container">
        <div style={styles.card}>
          <div style={{ fontSize: 64, marginBottom: 20 }}>🚀</div>
          <h1 style={styles.title}>{state.name} Real Estate CE — Launching Soon</h1>
          <p style={styles.desc}>
            We're bringing the same affordable, {state.regulator}-approved online continuing education
            to {state.name} that Arizona agents already use. Enter your email below to be the first
            to know when {state.name} courses go live.
          </p>

          <form style={styles.form} onSubmit={(e) => {
            e.preventDefault();
            const email = e.target.email.value;
            window.location.href = `mailto:help@steadyeducation.com?subject=Notify%20me%20when%20${encodeURIComponent(state.name)}%20launches&body=${encodeURIComponent('Email: ' + email + '\nState: ' + state.name)}`;
          }}>
            <input
              className="input"
              type="email"
              name="email"
              placeholder="your@email.com"
              required
              style={{ maxWidth: 280 }}
            />
            <button type="submit" className="btn btn-primary">Notify Me</button>
          </form>

          <div style={styles.meta}>
            <div><strong>Regulator:</strong> {state.regulator}</div>
            {state.requiredHours && <div><strong>CE required:</strong> {state.requiredHours} hours every {state.cycleYears} years</div>}
          </div>

          <div style={styles.divider} />

          <div style={styles.currentBox}>
            <strong>Currently offering courses in:</strong>
            {LIVE_STATES.map(s => (
              <p key={s.slug} style={{ margin: '8px 0 0', color: 'var(--color-text-muted)', fontSize: 14 }}>
                {s.name} — {s.requiredHours} required hours for ${s.packagePrice}, {s.regulator}-approved, instant certificates.
              </p>
            ))}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 }}>
              {LIVE_STATES.map(s => (
                <Link key={s.slug} to={`/${s.slug}`} className="btn btn-secondary btn-sm">See {s.name} Courses →</Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: { background: '#FAF7F2', minHeight: '100vh', padding: '60px 0' },
  card: { background: 'white', border: '1px solid var(--color-border)', borderRadius: 20, padding: '48px 40px', maxWidth: 640, margin: '0 auto', textAlign: 'center', boxShadow: 'var(--shadow-sm)' },
  title: { fontFamily: 'var(--font-display)', fontSize: 30, color: 'var(--forest-700)', marginBottom: 16, lineHeight: 1.2 },
  desc: { fontSize: 16, color: 'var(--slate-600)', lineHeight: 1.7, marginBottom: 28 },
  form: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 },
  meta: { display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', fontSize: 13, color: 'var(--slate-600)' },
  divider: { height: 1, background: 'var(--color-border)', margin: '32px 0' },
  currentBox: { background: 'var(--tan-50)', border: '1px solid #b39e89', borderRadius: 12, padding: 20 },
};
